import * as React from 'react';
import { Box, Stack, Typography, IconButton, Dialog, DialogContent, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';

type SingleImageUploadProps = {
  onChange?: (file: File | null) => void;
  initialUrl?: string | null;
  accept?: string;        // 'image/jpeg,image/png'
  label?: React.ReactNode;
  errorText?: string;
};

export default function SingleImageUpload({
  onChange,
  initialUrl = null,
  accept = 'image/jpeg,image/png',
  errorText,
}: SingleImageUploadProps) {
  const { t } = useTranslation('common');
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [isOver, setIsOver] = React.useState(false);
  const [preview, setPreview] = React.useState<string | null>(initialUrl);
  const [openView, setOpenView] = React.useState(false);
  const blobUrlRef = React.useRef<string | null>(null);
  const userPickedRef = React.useRef(false);

  const openPicker = () => {
    if (inputRef.current) inputRef.current.value = '';
    inputRef.current?.click();
  };

  const setFile = (file: File | null) => {
    // cleanup blob เดิม
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    if (file) {
      const url = URL.createObjectURL(file);
      blobUrlRef.current = url;
      setPreview(url);
      userPickedRef.current = true;
      onChange?.(file);
    } else {
      setPreview(null);
      userPickedRef.current = false;
      onChange?.(null);
    }
  };

  const whitelist = React.useMemo(
    () => accept.split(',').map(s => s.trim().toLowerCase()),
    [accept]
  );

  const isAllowed = (f: File) => {
    const name = f.name.toLowerCase();
    const type = (f.type || '').toLowerCase();
    const okMime = whitelist.includes(type) || (type === 'image/jpeg' && whitelist.includes('image/jpg'));
    const okExt = whitelist.some(token => {
      const ext = token.startsWith('image/') ? '.' + token.split('/')[1] : token.startsWith('.') ? token : '.' + token;
      return name.endsWith(ext) || (ext === '.jpg' && name.endsWith('.jpeg')) || (ext === '.jpeg' && name.endsWith('.jpg'));
    });
    return okMime || okExt;
  };

  const handleFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const f = files[0];
    if (isAllowed(f)) setFile(f);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOver(false);
    handleFiles(e.dataTransfer.files);
  };

  React.useEffect(() => {
    if (!userPickedRef.current) {
      setPreview(initialUrl ?? null);
    }
  }, [initialUrl]);

  React.useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  return (
    <>
    

      {preview ? (
        <Box
          sx={{
            position: 'relative',
            width: 132, height: 132,
            borderRadius: 2,
            border: '1px solid', borderColor: 'divider',
            overflow: 'hidden',
            '&:hover .overlay': { opacity: 1 },
          }}
        >
          <Box component="img" src={preview} alt="preview" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <Box
            className="overlay"
            sx={{
              position: 'absolute', inset: 0,
              bgcolor: 'rgba(0,0,0,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
              opacity: 0, transition: 'opacity .15s ease',
            }}
          >
            <Tooltip title={t('image.view')}>
              <IconButton onClick={() => setOpenView(true)} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <VisibilityIcon sx={{ color: '#fff' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('image.delete')}>
              <IconButton onClick={() => setFile(null)} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                <DeleteOutlineIcon sx={{ color: '#fff' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ) : (
        <Box
          onClick={openPicker}
          onDragOver={(e) => { e.preventDefault(); setIsOver(true); }}
          onDragLeave={() => setIsOver(false)}
          onDrop={onDrop}
          sx={(theme) => ({
            cursor: 'pointer',
            borderRadius: 2,
            p: 3, width: 240,
            border: '2px dashed',
            borderColor: isOver ? 'success.main' : theme.palette.grey[300],
            bgcolor: isOver ? 'success.50' : 'transparent',
            transition: 'all .15s ease',
            textAlign: 'center',
            '&:hover': { borderColor: 'success.main', bgcolor: 'success.50' },
          })}
        >
          <Stack alignItems="center" spacing={1}>
            <Box component="img" src="/static/icons/upload.svg" alt="" sx={{ width: 36, height: 36 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t('image.drop.hint')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('image.supported.types', { types: '.jpg, .png' })}
            </Typography>
          </Stack>
        </Box>
      )}

      {errorText && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
          {errorText}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => handleFiles(e.currentTarget.files)}
        style={{ display: 'none' }}
      />

      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="sm">
        <DialogContent sx={{ p: 0 }}>
          {preview && <Box component="img" src={preview} alt="preview-large" sx={{ width: '100%', height: 'auto', display: 'block' }} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
